/** Lengthy certificate examples for Sequences/Series and Conic Sections. */
import { EightExamples } from "./calcAgLengthyExamples";

export { EightExamples };

export const SERIES_P1_EXAMPLES = [
  {
    number: 1, tier: "Basic", title: "Limit of a sequence",
    setup: "Show that $a_n=(2n+1)/(n+3)\\to 2$.",
    steps: [
      { text: "Divide numerator and denominator by $n$: $a_n=(2+1/n)/(1+3/n)$.", why: "Prepare for $n\\to\\infty$." },
      { text: "As $n\\to\\infty$, $1/n\\to 0$ and $3/n\\to 0$.", why: "Standard null sequences." },
      { text: "Hence $a_n\\to(2+0)/(1+0)=2$.", why: "Algebra of limits." },
      { text: "So the sequence converges to $2$.", why: "Definition of sequence limit." },
      { text: "Check numerically: $a_{100}=201/103\\approx 1.95$ near $2$.", why: "Sanity check." },
      { text: "For large $n$, $a_n$ behaves like $2n/n=2$.", why: "Leading-term intuition." },
    ],
    result: "$\\lim a_n=2$.",
    check: "$a_{1000}=2001/1003\\approx 1.995$.",
    mistake: "Cancelling only part of the expression or treating $n$ as continuous without justification.",
    life: "Loan amortization schedules and sensor smoothing both watch whether a discrete sequence of readings settles near a target value — the same limit idea.",
  },
  {
    number: 2, tier: "Basic", title: "Geometric series sum",
    setup: "Evaluate $\\sum_{n=0}^{\\infty}(1/2)^n$.",
    steps: [
      { text: "Identify $a=1$, $r=1/2$.", why: "Match $\\sum ar^n$." },
      { text: "Check $|r|=1/2<1$, so the series converges.", why: "Geometric criterion." },
      { text: "Use $\\sum_{n=0}^{\\infty}ar^n=a/(1-r)$.", why: "Closed form." },
      { text: "Compute $1/(1-1/2)=2$.", why: "Arithmetic." },
      { text: "Partial sums $s_N=2(1-(1/2)^{N+1})\\to 2$.", why: "Finite geometric formula." },
      { text: "Thus the infinite sum is $2$.", why: "Limit of $s_N$." },
    ],
    result: "Sum $=2$.",
    check: "$s_5=1+1/2+\\cdots+1/32=63/32\\approx 1.97$.",
    mistake: "Using $a/(1+r)$ or starting the index wrong.",
  },
  {
    number: 3, tier: "Medium", title: "Term test divergence",
    setup: "Does $\\sum n/(n+1)$ converge?",
    steps: [
      { text: "Look at $a_n=n/(n+1)$.", why: "Apply the necessary condition." },
      { text: "$a_n=1/(1+1/n)\\to 1\\neq 0$.", why: "Divide by $n$." },
      { text: "Since $a_n\\not\\to 0$, the series diverges.", why: "Term test." },
      { text: "Partial sums grow roughly like adding $1$ each time.", why: "Heuristic." },
      { text: "So no finite sum exists.", why: "Conclusion." },
      { text: "Contrast with $\\sum 1/n^2$ where terms do go to $0$.", why: "Term test is necessary, not sufficient." },
    ],
    result: "Diverges by the term test.",
    check: "$a_{1000}=1000/1001\\approx 0.999$.",
    mistake: "Thinking $a_n\\to 0$ alone proves convergence.",
  },
  {
    number: 4, tier: "Medium", title: "Telescoping series",
    setup: "Sum $\\sum_{n=1}^{\\infty}1/(n(n+1))$.",
    steps: [
      { text: "Decompose: $1/(n(n+1))=1/n-1/(n+1)$.", why: "Partial fractions." },
      { text: "Write $s_N=(1-1/2)+(1/2-1/3)+\\cdots+(1/N-1/(N+1))$.", why: "Expand." },
      { text: "Cancel intermediate terms: $s_N=1-1/(N+1)$.", why: "Telescope." },
      { text: "Let $N\\to\\infty$: $s_N\\to 1$.", why: "Limit of partial sums." },
      { text: "Hence the series converges to $1$.", why: "Definition of series sum." },
      { text: "Compare with $p=2$ comparison: same conclusion.", why: "Cross-check." },
    ],
    result: "Sum $=1$.",
    check: "$s_{10}=1-1/11=10/11$.",
    mistake: "Forgetting the leftover $-1/(N+1)$ term.",
  },
  {
    number: 5, tier: "Medium", title: "p-series",
    setup: "Decide convergence of $\\sum 1/n^{3/2}$.",
    steps: [
      { text: "Recognize a $p$-series with $p=3/2$.", why: "Form $1/n^p$." },
      { text: "Compare $p=3/2$ with $1$.", why: "$p$-test threshold." },
      { text: "Since $3/2>1$, the series converges.", why: "$p$-test." },
      { text: "Integral $\\int_1^\\infty x^{-3/2}\\,dx$ also converges.", why: "Integral test twin." },
      { text: "Terms go to $0$ faster than $1/n$.", why: "Intuition." },
      { text: "So the infinite sum is finite (exact value not required).", why: "Convergence vs evaluation." },
    ],
    result: "Converges ($p=3/2>1$).",
    check: "Contrast $\\sum 1/\\sqrt{n}$ with $p=1/2<1$ diverges.",
    mistake: "Mixing up the inequality $p>1$ vs $p<1$.",
  },
  {
    number: 6, tier: "Advanced", title: "Geometric with nonzero first term",
    setup: "Sum $\\sum_{n=0}^{\\infty}3(2/5)^n$.",
    steps: [
      { text: "$a=3$, $r=2/5$.", why: "Identify parameters." },
      { text: "$|r|=2/5<1$ ⇒ converges.", why: "Geometric test." },
      { text: "Sum $=3/(1-2/5)=3/(3/5)=5$.", why: "Formula." },
      { text: "Check $s_0=3$, $s_1=3+6/5=4.2$ climbing toward $5$.", why: "Partial sums." },
      { text: "Remainder after $N$ is $a r^{N+1}/(1-r)$.", why: "Error estimate." },
      { text: "Final answer $5$.", why: "Close." },
    ],
    result: "Sum $=5$.",
    check: "$3/(1-0.4)=5$.",
    mistake: "Writing $3/(1+2/5)$.",
  },
  {
    number: 7, tier: "Advanced", title: "Comparison",
    setup: "Show $\\sum 1/(n^2+1)$ converges.",
    steps: [
      { text: "Note $0<1/(n^2+1)<1/n^2$.", why: "Denominator larger ⇒ fraction smaller." },
      { text: "$\\sum 1/n^2$ converges ($p=2>1$).", why: "Known $p$-series." },
      { text: "By direct comparison, $\\sum 1/(n^2+1)$ converges.", why: "Comparison test." },
      { text: "Limit comparison: $a_n/b_n\\to 1$ with $b_n=1/n^2$.", why: "Alternative route." },
      { text: "Same conclusion.", why: "Consistency." },
      { text: "Cannot sum in elementary closed form easily — convergence is enough.", why: "Exam scope." },
    ],
    result: "Converges by comparison with $\\sum 1/n^2$.",
    check: "Limit comparison limit $=1>0$ finite.",
    mistake: "Comparing with the divergent harmonic series.",
  },
  {
    number: 8, tier: "Advanced", title: "Divergent harmonic cousin",
    setup: "Show $\\sum_{n=2}^{\\infty}1/(n\\ln n)$ diverges.",
    steps: [
      { text: "Consider $f(x)=1/(x\\ln x)$ for $x\\ge 2$.", why: "Integral test setup." },
      { text: "$f$ is positive, continuous, decreasing.", why: "Hypotheses." },
      { text: "$\\int_2^\\infty dx/(x\\ln x)=\\lim_{b\\to\\infty}[\\ln\\ln x]_2^b=\\infty$.", why: "Substitution $u=\\ln x$." },
      { text: "Integral diverges ⇒ series diverges.", why: "Integral test." },
      { text: "Cauchy condensation gives the same result.", why: "Optional cross-check." },
      { text: "Terms → 0 slowly, so term test is inconclusive.", why: "Why a stronger test is needed." },
    ],
    result: "Diverges (integral test).",
    check: "$\\ln\\ln b\\to\\infty$.",
    mistake: "Stopping after noticing $a_n\\to 0$.",
  },
];

export const SERIES_P2_EXAMPLES = [
  {
    number: 1, tier: "Basic", title: "Ratio test",
    setup: "Test $\\sum n/3^n$.",
    steps: [
      { text: "Set $a_n=n/3^n$.", why: "Identify terms." },
      { text: "Compute $|a_{n+1}/a_n|=(n+1)/n\\cdot 1/3\\to 1/3$.", why: "Ratio." },
      { text: "$L=1/3<1$ ⇒ absolute convergence.", why: "Ratio test." },
      { text: "Exponential decay beats linear growth.", why: "Intuition." },
      { text: "Series converges.", why: "Conclusion." },
      { text: "Root test also yields $L=1/3$.", why: "Cross-check." },
    ],
    result: "Converges (absolutely).",
    check: "$L=1/3$.",
    mistake: "Forgetting the $(n+1)/n\\to 1$ factor.",
    life: "Discounted cash-flow models $\\sum n r^n$ appear in finance; the ratio test is why a growth factor $|r|<1$ keeps the present value finite.",
  },
  {
    number: 2, tier: "Medium", title: "Alternating harmonic",
    setup: "Classify $\\sum_{n=1}^{\\infty}(-1)^{n+1}/n$.",
    steps: [
      { text: "Let $b_n=1/n$: positive, decreasing, $\\to 0$.", why: "AST hypotheses." },
      { text: "AST ⇒ the alternating series converges.", why: "Leibniz test." },
      { text: "But $\\sum|(-1)^{n+1}/n|=\\sum 1/n$ diverges.", why: "Harmonic." },
      { text: "Therefore convergence is conditional, not absolute.", why: "Definition." },
      { text: "Rearrangements can change the sum.", why: "Riemann rearrangement." },
      { text: "The sum equals $\\ln 2$.", why: "Known Taylor endpoint." },
    ],
    result: "Conditionally convergent (sum $\\ln 2$).",
    check: "Partial alternating sums approach $\\approx 0.693$.",
    mistake: "Calling it absolutely convergent.",
  },
  {
    number: 3, tier: "Medium", title: "Radius of $\\sum x^n$",
    setup: "Find the radius of $\\sum_{n=0}^{\\infty}x^n$.",
    steps: [
      { text: "Ratio: $|a_{n+1}/a_n|=|x|$.", why: "Coefficient ratio." },
      { text: "Need $|x|<1$ for $L<1$.", why: "Ratio test." },
      { text: "Radius $R=1$.", why: "Definition of $R$." },
      { text: "At $x=1$: harmonic divergent; at $x=-1$: alternating harmonic converges.", why: "Endpoints." },
      { text: "Interval of convergence is $-1\\le x<1$.", why: "Combine." },
      { text: "Sum is $1/(1-x)$ for $|x|<1$.", why: "Geometric closed form." },
    ],
    result: "$R=1$; converges on $[-1,1)$.",
    check: "Hadamard: $|c_n|^{1/n}=1$.",
    mistake: "Claiming convergence for all $x$.",
  },
  {
    number: 4, tier: "Advanced", title: "Exponential series radius",
    setup: "Radius of $\\sum x^n/n!$.",
    steps: [
      { text: "$|a_{n+1}/a_n|=|x|/(n+1)\\to 0$.", why: "Ratio." },
      { text: "$L=0<1$ for every fixed $x$.", why: "Ratio test." },
      { text: "Hence $R=\\infty$.", why: "Entire function." },
      { text: "The sum is $e^x$ for all real $x$.", why: "Taylor of exp." },
      { text: "Termwise differentiation recovers the same series.", why: "Consistency." },
      { text: "Factorials dominate any exponential growth in $|x|^n$.", why: "Intuition." },
    ],
    result: "$R=\\infty$; sum $e^x$.",
    check: "Same radius for $\\sin$ and $\\cos$ series.",
    mistake: "Claiming $R=1$ by analogy with geometric series.",
  },
  {
    number: 5, tier: "Advanced", title: "Root test",
    setup: "Test $\\sum (2/5)^n$.",
    steps: [
      { text: "$|a_n|^{1/n}=2/5$.", why: "Root." },
      { text: "$L=2/5<1$ ⇒ absolute convergence.", why: "Root test." },
      { text: "Also geometric with $r=2/5$.", why: "Recognition." },
      { text: "Sum $=1/(1-2/5)=5/3$.", why: "Closed form." },
      { text: "Both tests agree.", why: "Cross-check." },
      { text: "Done.", why: "Close." },
    ],
    result: "Converges; sum $5/3$.",
    check: "Ratio gives same $L$.",
    mistake: "Using $L>1$ criterion backwards.",
  },
  {
    number: 6, tier: "Advanced", title: "Conditional vs absolute",
    setup: "Classify $\\sum (-1)^n/\\sqrt{n}$.",
    steps: [
      { text: "$b_n=1/\\sqrt{n}$ decreases to $0$.", why: "AST setup." },
      { text: "AST ⇒ convergence.", why: "Leibniz." },
      { text: "$\\sum 1/\\sqrt{n}$ is $p=1/2<1$ divergent.", why: "Absolute series." },
      { text: "So the series is conditionally convergent.", why: "Definition." },
      { text: "Cannot rearrange freely.", why: "Warning." },
      { text: "Error after $N$ terms bounded by next term $1/\\sqrt{N+1}$.", why: "AST remainder." },
    ],
    result: "Conditionally convergent.",
    check: "AST hypotheses hold.",
    mistake: "Declaring absolute convergence because signs alternate.",
  },
  {
    number: 7, tier: "Advanced", title: "Differentiating geometric",
    setup: "Find $\\sum_{n=1}^{\\infty}n x^{n}$ for $|x|<1$.",
    steps: [
      { text: "Start from $\\sum_{n=0}^{\\infty}x^n=1/(1-x)$.", why: "Geometric." },
      { text: "Differentiate: $\\sum_{n=1}^{\\infty}n x^{n-1}=1/(1-x)^2$.", why: "Termwise on $|x|<1$." },
      { text: "Multiply by $x$: $\\sum n x^n=x/(1-x)^2$.", why: "Algebra." },
      { text: "Valid for $|x|<1$.", why: "Preserve radius." },
      { text: "At $x=1/2$: sum $=2$.", why: "Quick check." },
      { text: "Formula used in generating functions.", why: "Context." },
    ],
    result: "$\\sum n x^n=x/(1-x)^2$ for $|x|<1$.",
    check: "$x=1/2$ gives $2$.",
    mistake: "Applying the formula at $|x|\\ge 1$.",
  },
  {
    number: 8, tier: "Advanced", title: "Endpoint subtlety",
    setup: "Discuss $\\sum (-1)^{n+1}x^n/n$ at $x=1$.",
    steps: [
      { text: "Inside $|x|<1$ this is $-\\ln(1-x)$ wait: actually $\\ln(1+x)$ form.", why: "Identify series." },
      { text: "At $x=1$: alternating harmonic converges to $\\ln 2$.", why: "Endpoint." },
      { text: "At $x=-1$: becomes $-\\sum 1/n$ divergent.", why: "Other endpoint." },
      { text: "Radius is $1$; interval $(-1,1]$.", why: "Combine." },
      { text: "Abel theorem links the endpoint sum to the continuous log.", why: "Advanced remark." },
      { text: "Always check endpoints separately from the radius.", why: "Moral." },
    ],
    result: "Converges at $x=1$ to $\\ln 2$; diverges at $x=-1$.",
    check: "AST at $x=1$; harmonic at $x=-1$.",
    mistake: "Assuming both endpoints behave the same.",
  },
];

export const CONICS_P1_EXAMPLES = [
  {
    number: 1, tier: "Basic", title: "Distance and midpoint",
    setup: "Points $A(1,2)$, $B(4,6)$: distance and midpoint.",
    steps: [
      { text: "Distance $=\\sqrt{(4-1)^2+(6-2)^2}=\\sqrt{9+16}=5$.", why: "Distance formula." },
      { text: "Midpoint $=((1+4)/2,(2+6)/2)=(2.5,4)$.", why: "Average coordinates." },
      { text: "Slope $m=(6-2)/(4-1)=4/3$.", why: "Optional check." },
      { text: "Segment length matches a $3$-$4$-$5$ triangle.", why: "Geometry." },
      { text: "Report distance $5$, midpoint $(5/2,4)$.", why: "Exact form." },
      { text: "Done.", why: "Close." },
    ],
    result: "Distance $5$; midpoint $(5/2,4)$.",
    check: "$3$-$4$-$5$ right triangle.",
    mistake: "Forgetting to square both differences.",
    life: "GPS receivers and surveying apps compute the same planar distance formula between coordinates (locally flat approximation).",
  },
  {
    number: 2, tier: "Basic", title: "Circle standard form",
    setup: "Rewrite $x^2+y^2-4x+6y-3=0$ as a circle.",
    steps: [
      { text: "Group: $(x^2-4x)+(y^2+6y)=3$.", why: "Prepare completing the square." },
      { text: "Complete: $(x-2)^2-4+(y+3)^2-9=3$.", why: "Add $(2)^2$ and $(3)^2$." },
      { text: "$(x-2)^2+(y+3)^2=16$.", why: "Move constants." },
      { text: "Center $(2,-3)$, radius $4$.", why: "Read standard form." },
      { text: "Check a point: $(2,1)$: $0+16=16$ on the circle.", why: "Verify." },
      { text: "Done.", why: "Close." },
    ],
    result: "Center $(2,-3)$, radius $4$.",
    check: "$(2-2)^2+(-3+3)^2=0$ is the center interior.",
    mistake: "Sign errors when completing $(y+3)^2$.",
  },
  {
    number: 3, tier: "Medium", title: "Parabola focus",
    setup: "For $y^2=12x$, find focus and directrix.",
    steps: [
      { text: "Write $4a=12$ so $a=3$.", why: "Match $y^2=4ax$." },
      { text: "Focus is $(a,0)=(3,0)$.", why: "Standard." },
      { text: "Directrix is $x=-a$, i.e. $x=-3$.", why: "Standard." },
      { text: "Vertex at origin; opens right.", why: "Orientation." },
      { text: "Latus rectum length $4a=12$.", why: "Extra data." },
      { text: "Eccentricity $e=1$.", why: "Parabola." },
    ],
    result: "Focus $(3,0)$; directrix $x=-3$.",
    check: "Point $(3,6)$: distance to focus $=$ distance to directrix $=6$.",
    mistake: "Using $a=12$ instead of $a=3$.",
  },
  {
    number: 4, tier: "Medium", title: "Ellipse foci",
    setup: "For $x^2/25+y^2/9=1$, find $e$ and foci.",
    steps: [
      { text: "$a^2=25$, $b^2=9$ so $a=5$, $b=3$.", why: "Read denominators." },
      { text: "$c=\\sqrt{a^2-b^2}=\\sqrt{16}=4$.", why: "Ellipse relation." },
      { text: "$e=c/a=4/5$.", why: "Definition." },
      { text: "Foci $(\\pm c,0)=(\\pm 4,0)$.", why: "Major axis along $x$." },
      { text: "Vertices $(\\pm 5,0)$.", why: "Extra." },
      { text: "String property: $PF_1+PF_2=10$.", why: "$2a$." },
    ],
    result: "$e=4/5$; foci $(\\pm 4,0)$.",
    check: "$4/5<1$ confirms ellipse.",
    mistake: "Swapping $a$ and $b$ when $b>a$.",
  },
  {
    number: 5, tier: "Medium", title: "Hyperbola asymptotes",
    setup: "Asymptotes of $x^2/16-y^2/9=1$.",
    steps: [
      { text: "$a^2=16$, $b^2=9$.", why: "Identify." },
      { text: "Asymptotes $y=\\pm(b/a)x=\\pm(3/4)x$.", why: "Standard formula." },
      { text: "Opens left-right.", why: "Form $x^2/a^2-y^2/b^2$." },
      { text: "$c=\\sqrt{a^2+b^2}=5$.", why: "Hyperbola relation." },
      { text: "$e=c/a=5/4>1$.", why: "Check." },
      { text: "Foci $(\\pm 5,0)$.", why: "Extra." },
    ],
    result: "$y=\\pm(3/4)x$.",
    check: "$e=5/4>1$.",
    mistake: "Writing $\\pm(a/b)x$.",
  },
  {
    number: 6, tier: "Advanced", title: "Focus–directrix check",
    setup: "Verify $(3,6)$ lies on $y^2=12x$ via $PF=e\\cdot PD$ with $e=1$.",
    steps: [
      { text: "Focus $F(3,0)$, directrix $x=-3$.", why: "From $a=3$." },
      { text: "$P(3,6)$: $PF=\\sqrt{0+36}=6$.", why: "Distance." },
      { text: "Distance to directrix $=|3-(-3)|=6$.", why: "Horizontal distance." },
      { text: "$PF=1\\cdot PD$.", why: "Parabola definition." },
      { text: "Also $6^2=12\\cdot 3$ checks the Cartesian equation.", why: "Algebra." },
      { text: "Point is on the parabola.", why: "Conclusion." },
    ],
    result: "Definition satisfied; point on the curve.",
    check: "$36=36$.",
    mistake: "Measuring distance to directrix diagonally.",
  },
  {
    number: 7, tier: "Advanced", title: "Tangent to circle",
    setup: "Tangent to $x^2+y^2=25$ at $(3,4)$.",
    steps: [
      { text: "For $x^2+y^2=r^2$, tangent is $xx_0+yy_0=r^2$.", why: "Standard." },
      { text: "Here $3x+4y=25$.", why: "Plug point." },
      { text: "Radius slope $4/3$; tangent slope $-3/4$.", why: "Negative reciprocal." },
      { text: "Point-slope: $y-4=-(3/4)(x-3)$ expands to same line.", why: "Cross-check." },
      { text: "Normal is the radius line.", why: "Geometry." },
      { text: "Done.", why: "Close." },
    ],
    result: "$3x+4y=25$.",
    check: "$(3,4)$ satisfies $9+16=25$.",
    mistake: "Using $x/x_0+y/y_0=1$ incorrectly.",
  },
  {
    number: 8, tier: "Advanced", title: "Eccentricity table",
    setup: "State $e$ for circle, ellipse, parabola, hyperbola.",
    steps: [
      { text: "Circle: $e=0$.", why: "All points equidistant from center." },
      { text: "Ellipse: $0<e<1$.", why: "Definition." },
      { text: "Parabola: $e=1$.", why: "Definition." },
      { text: "Hyperbola: $e>1$.", why: "Definition." },
      { text: "Focus–directrix unifies all four.", why: "Big picture." },
      { text: "Use $e$ to classify after reducing to standard form.", why: "Exam strategy." },
    ],
    result: "$e=0$, $(0,1)$, $1$, $>1$ respectively.",
    check: "Memorize the four cases.",
    mistake: "Assigning $e=1$ to ellipses.",
  },
];

export const CONICS_P2_EXAMPLES = [
  {
    number: 1, tier: "Basic", title: "Discriminant classification",
    setup: "Classify $x^2+xy+y^2-1=0$ using $B^2-4AC$.",
    steps: [
      { text: "$A=1$, $B=1$, $C=1$.", why: "Read coefficients." },
      { text: "$B^2-4AC=1-4=-3<0$.", why: "Compute discriminant." },
      { text: "Nondegenerate ⇒ ellipse type (after rotation).", why: "Classification." },
      { text: "Rotate to remove $xy$ via $\\cot 2\\theta=(A-C)/B=0$ so $2\\theta=\\pi/2$.", why: "Angle." },
      { text: "New axes make it a standard ellipse.", why: "Outcome." },
      { text: "Not a hyperbola or parabola.", why: "Conclusion." },
    ],
    result: "Ellipse type ($B^2-4AC<0$).",
    check: "$-3<0$.",
    mistake: "Ignoring the $xy$ term.",
    life: "Antenna and dish designers use parabolic reflectors; planetary orbits are ellipses with the sun at a focus — both are conic sections in engineering and astronomy.",
  },
  {
    number: 2, tier: "Medium", title: "Parabola optics",
    setup: "Explain why a satellite dish is parabolic.",
    steps: [
      { text: "Incoming rays parallel to the axis strike the dish.", why: "Far-field signal." },
      { text: "Parabola reflection law sends them through the focus.", why: "Optical property." },
      { text: "Receiver placed at the focus gathers energy.", why: "Engineering." },
      { text: "Cartesian model $y^2=4ax$ places focus at $(a,0)$.", why: "Math link." },
      { text: "Same property used in headlights (reverse direction).", why: "Dual use." },
      { text: "Thus analytic geometry designs the hardware.", why: "Moral." },
    ],
    result: "Focus concentration of parallel rays.",
    check: "Focus–directrix with $e=1$.",
    mistake: "Placing the receiver at the vertex.",
  },
  {
    number: 3, tier: "Medium", title: "Ellipse string construction",
    setup: "Why does a gardener’s string method draw an ellipse?",
    steps: [
      { text: "Fix two foci $F_1,F_2$ with distance $2c$.", why: "Setup." },
      { text: "Loop of length $2a>2c$.", why: "String length." },
      { text: "Pencil keeps $PF_1+PF_2=2a$.", why: "Constant sum." },
      { text: "That is the definition of an ellipse.", why: "AG definition." },
      { text: "$b^2=a^2-c^2$ gives the minor axis.", why: "Relation." },
      { text: "Eccentricity $e=c/a$ measures flatness.", why: "Interpretation." },
    ],
    result: "Constant sum of distances ⇒ ellipse.",
    check: "Circle when $F_1=F_2$.",
    mistake: "Using string length shorter than $2c$.",
  },
  {
    number: 4, tier: "Advanced", title: "Hyperbola navigation",
    setup: "LORAN uses time-difference of radio signals. Why hyperbolas?",
    steps: [
      { text: "Time difference × speed of light = distance difference.", why: "Physics." },
      { text: "Locus of constant $|PF_1-PF_2|$ is a hyperbola.", why: "Definition." },
      { text: "Two station pairs give two hyperbolas; intersection is the position.", why: "Fix." },
      { text: "Eccentricity $e>1$ matches the hyperbola case.", why: "Classification." },
      { text: "Analytic geometry turns timing into coordinates.", why: "Moral." },
      { text: "Modern GNSS uses related multilateration ideas.", why: "Context." },
    ],
    result: "Constant distance difference ⇒ hyperbola locus.",
    check: "$|d_1-d_2|=2a$.",
    mistake: "Using ellipse (sum) instead of difference.",
  },
  {
    number: 5, tier: "Advanced", title: "Kepler ellipse",
    setup: "State the conic law of orbits.",
    steps: [
      { text: "Inverse-square gravity ⇒ planar orbit.", why: "Newtonian mechanics." },
      { text: "Orbit is a conic with one focus at the sun.", why: "Kepler–Newton." },
      { text: "Bound orbits are ellipses ($e<1$).", why: "Energy." },
      { text: "Escape trajectories are parabolas ($e=1$) or hyperbolas ($e>1$).", why: "Energy cases." },
      { text: "Semi-major axis relates to period via Kepler’s third law.", why: "Extra." },
      { text: "Analytic geometry is the language of celestial mechanics.", why: "Close." },
    ],
    result: "Orbits are focus–directrix conics.",
    check: "Earth $e\\approx 0.017$ nearly circular.",
    mistake: "Claiming all orbits are circles.",
  },
  {
    number: 6, tier: "Advanced", title: "Rotate to remove xy",
    setup: "For $xy=1$, find a rotation that standardizes it.",
    steps: [
      { text: "$A=0,B=1,C=0$ ⇒ $B^2-4AC=1>0$ hyperbola.", why: "Classify." },
      { text: "$\\cot 2\\theta=(A-C)/B=0$ ⇒ $2\\theta=\\pi/2$, $\\theta=\\pi/4$.", why: "Angle." },
      { text: "Rotate by $45^\\circ$: new coordinates $(u,v)$.", why: "Substitution." },
      { text: "Becomes $u^2-v^2=2$ (up to scaling) — rectangular hyperbola.", why: "Standard form." },
      { text: "Asymptotes become the new axes.", why: "Geometry." },
      { text: "Rotation removed the $xy$ term.", why: "Goal." },
    ],
    result: "Hyperbola; rotate $45^\\circ$ to standard form.",
    check: "Discriminant positive.",
    mistake: "Trying to complete the square without rotating.",
  },
  {
    number: 7, tier: "Advanced", title: "Condition for tangency",
    setup: "Find $c$ so $y=2x+c$ is tangent to $x^2+y^2=9$.",
    steps: [
      { text: "Substitute $y=2x+c$ into $x^2+y^2=9$.", why: "Intersection." },
      { text: "$x^2+(2x+c)^2=9$ ⇒ $5x^2+4cx+c^2-9=0$.", why: "Expand." },
      { text: "Tangency ⇒ discriminant $0$: $16c^2-20(c^2-9)=0$.", why: "One solution." },
      { text: "Simplify: $-4c^2+180=0$ ⇒ $c^2=45$.", why: "Solve." },
      { text: "$c=\\pm 3\\sqrt{5}$.", why: "Roots." },
      { text: "Formula $c^2=r^2(1+m^2)=9(1+4)=45$ matches.", why: "Shortcut." },
    ],
    result: "$c=\\pm 3\\sqrt{5}$.",
    check: "$c^2=r^2(1+m^2)$.",
    mistake: "Setting the linear coefficient to zero instead of the discriminant.",
  },
  {
    number: 8, tier: "Advanced", title: "Projectile parabola",
    setup: "Why is an ideal projectile path a parabola?",
    steps: [
      { text: "Horizontal velocity constant; vertical has acceleration $-g$.", why: "Newton." },
      { text: "$x=v_x t$, $y=v_y t-\\tfrac12 gt^2$.", why: "Kinematics." },
      { text: "Eliminate $t$: $y=(v_y/v_x)x-(g/(2v_x^2))x^2$.", why: "Algebra." },
      { text: "Quadratic in $x$ with no $xy$ term ⇒ parabola.", why: "Classification." },
      { text: "Axis vertical; vertex at max height.", why: "Geometry." },
      { text: "Air resistance breaks the perfect parabola.", why: "Model limit." },
    ],
    result: "$y=Ax-Bx^2$ is a parabola.",
    check: "$B^2-4AC$ with $A=-B_{\\mathrm{coeff}}$, $B_{xy}=0$, $C=0$.",
    mistake: "Calling the path an ellipse.",
  },
];
